/**
 * 🛡️ ARMOR (Tops)
 *
 * Body armor equipment items (15 total)
 * Level requirements: 5-90 (increments of 5)
 * Stat requirements: STR for heavy armor, INT for robes
 *
 * Level Distribution:
 * - Common (3): Lv 5, 10, 15
 * - Uncommon (3): Lv 20, 25, 30
 * - Rare (3): Lv 35, 40, 45
 * - Epic (3): Lv 50, 55, 60
 * - Legendary (3): Lv 70, 80, 90
 */

export const ARMOR = {
  // ============================================================
  // COMMON ARMOR (3) - Lv 5, 10, 15
  // ============================================================
  leatherVest: {
    id: 'leatherVest',
    name: 'Leather Vest',
    icon: '🧥',
    slot: 'top',
    rarity: 'common',
    levelReq: 5,
    requirements: { STR: 5 },
    description: 'Basic protection from cuts and scrapes.',
    stats: {
      defense: 2,
      health: 15,
    },
  },
  clothTunic: {
    id: 'clothTunic',
    name: 'Cloth Tunic',
    icon: '👕',
    slot: 'top',
    rarity: 'common',
    levelReq: 10,
    requirements: { INT: 10 },
    description: 'Light and comfortable, but offers little defense.',
    stats: {
      defense: 1,
      health: 20,
    },
  },
  paddedArmor: {
    id: 'paddedArmor',
    name: 'Padded Armor',
    icon: '🦺',
    slot: 'top',
    rarity: 'common',
    levelReq: 15,
    requirements: { STR: 18 },
    description: 'Quilted layers provide modest protection.',
    stats: {
      defense: 3,
      health: 12,
    },
  },

  // ============================================================
  // UNCOMMON ARMOR (3) - Lv 20, 25, 30
  // ============================================================
  chainmail: {
    id: 'chainmail',
    name: 'Chainmail',
    icon: '🛡️',
    slot: 'top',
    rarity: 'uncommon',
    levelReq: 20,
    requirements: { STR: 35 },
    description: 'Interlocking metal rings protect vital areas.',
    stats: {
      defense: 5,
      health: 30,
    },
  },
  scaleArmor: {
    id: 'scaleArmor',
    name: 'Scale Armor',
    icon: '🐉',
    slot: 'top',
    rarity: 'uncommon',
    levelReq: 25,
    requirements: { STR: 42 },
    description: 'Armor made from overlapping metal scales.',
    stats: {
      defense: 6,
      health: 25,
    },
  },
  studdedLeather: {
    id: 'studdedLeather',
    name: 'Studded Leather',
    icon: '🧥',
    slot: 'top',
    rarity: 'uncommon',
    levelReq: 30,
    requirements: { STR: 30, DEX: 25 },
    description: 'Reinforced leather with metal studs.',
    stats: {
      defense: 4,
      health: 35,
    },
  },

  // ============================================================
  // RARE ARMOR (3) - Lv 35, 40, 45
  // ============================================================
  plateArmor: {
    id: 'plateArmor',
    name: 'Plate Armor',
    icon: '🛡️',
    slot: 'top',
    rarity: 'rare',
    levelReq: 35,
    requirements: { STR: 70 },
    description: 'Full body protection of solid steel plates.',
    stats: {
      defense: 10,
      health: 55,
    },
  },
  dragonscale: {
    id: 'dragonscale',
    name: 'Dragonscale Mail',
    icon: '🐲',
    slot: 'top',
    rarity: 'rare',
    levelReq: 40,
    requirements: { STR: 65, DEX: 30 },
    description: 'Armor crafted from actual dragon scales.',
    stats: {
      defense: 9,
      health: 60,
    },
  },
  enchantedRobes: {
    id: 'enchantedRobes',
    name: 'Enchanted Robes',
    icon: '👘',
    slot: 'top',
    rarity: 'rare',
    levelReq: 45,
    requirements: { INT: 85 },
    description: 'Magically woven robes that deflect blows.',
    stats: {
      defense: 7,
      health: 65,
    },
  },

  // ============================================================
  // EPIC ARMOR (3) - Lv 50, 55, 60
  // ============================================================
  titaniumPlate: {
    id: 'titaniumPlate',
    name: 'Titanium Plate',
    icon: '⚜️',
    slot: 'top',
    rarity: 'epic',
    levelReq: 50,
    requirements: { STR: 105 },
    description: 'Impossibly light yet unbreakably strong.',
    stats: {
      defense: 16,
      health: 95,
    },
  },
  celestialRobes: {
    id: 'celestialRobes',
    name: 'Celestial Robes',
    icon: '✨',
    slot: 'top',
    rarity: 'epic',
    levelReq: 55,
    requirements: { INT: 115 },
    description: 'Woven from stardust and moonlight.',
    stats: {
      defense: 14,
      health: 105,
    },
  },
  demonhideArmor: {
    id: 'demonhideArmor',
    name: 'Demonhide Armor',
    icon: '😈',
    slot: 'top',
    rarity: 'epic',
    levelReq: 60,
    requirements: { STR: 100, LUK: 40 },
    description: 'Tanned from the hide of a greater demon.',
    stats: {
      defense: 15,
      health: 100,
    },
  },

  // ============================================================
  // LEGENDARY ARMOR (3) - Lv 70, 80, 90
  // ============================================================
  eternalGuardian: {
    id: 'eternalGuardian',
    name: 'Eternal Guardian',
    icon: '🛡️',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 70,
    requirements: { STR: 145 },
    description: 'Armor that has protected heroes across the ages.',
    stats: {
      defense: 28,
      health: 175,
    },
  },
  voidPlate: {
    id: 'voidPlate',
    name: 'Void Plate',
    icon: '🌌',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 80,
    requirements: { STR: 160, INT: 60 },
    description: 'Forged in the space between realities.',
    stats: {
      defense: 30,
      health: 160,
    },
  },
  divineMail: {
    id: 'divineMail',
    name: 'Divine Mail',
    icon: '⚜️',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 90,
    requirements: { STR: 180, INT: 80 },
    description: 'A gift from the gods themselves.',
    stats: {
      defense: 26,
      health: 190,
    },
  },
};
